import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import RouteIcon from '@mui/icons-material/Route';
import SaveIcon from '@mui/icons-material/Save';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PlaceIcon from '@mui/icons-material/Place';
import IKRouteMap from './IKRouteMap';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
const fmtKm = (value) => `${Number(value || 0).toFixed(2)} km`;
const fmtMinutes = (value) => `${Number(value || 0).toFixed(0)} min`;

const emptyPoint = (idx) => ({
  label: idx === 0 ? 'Départ' : `Point ${idx + 1}`,
  labelMode: 'auto',
  organizationName: '',
  siteName: '',
  address: '',
  selectionOrganizationId: '',
  sourceType: 'manual',
  selectionType: '',
});

const organizationSelectionTypes = ['client', 'fournisseur', 'partenaire'];
const selectionTypeLabels = {
  home: 'Domicile',
  client: 'Client',
  fournisseur: 'Fournisseur',
  partenaire: 'Partenaire',
  manual: 'Manuel',
};
const selectionTypeIcons = {
  home: HomeIcon,
  client: CorporateFareIcon,
  fournisseur: LocalShippingIcon,
  partenaire: HandshakeIcon,
  manual: PlaceIcon,
};

function normalizeSelectionType(point) {
  if (point?.selectionType === '') return '';
  if (point?.selectionType) return point.selectionType;
  if (point?.sourceType === 'home') return 'home';
  if (point?.sourceType === 'organization') return 'client';
  return 'manual';
}

function getDefaultPointLabel(idx) {
  return idx === 0 ? 'Départ' : `Point ${idx + 1}`;
}

function isSyntheticReturnPoint(firstPoint, lastPoint) {
  if (!firstPoint || !lastPoint) return false;

  const normalizedFirstAddress = String(firstPoint.address || '').trim().toLowerCase();
  const normalizedLastAddress = String(lastPoint.address || '').trim().toLowerCase();
  const normalizedLastLabel = String(lastPoint.label || '').trim().toLowerCase();
  const firstSelectionType = normalizeSelectionType(firstPoint);
  const lastSelectionType = normalizeSelectionType(lastPoint);

  return (
    normalizedLastLabel.endsWith('(retour)')
    && normalizedFirstAddress !== ''
    && normalizedFirstAddress === normalizedLastAddress
    && firstSelectionType === lastSelectionType
  );
}

function stripSyntheticReturnPoint(points = []) {
  if (!Array.isArray(points) || points.length < 2) return Array.isArray(points) ? points : [];
  const nextPoints = [...points];
  if (isSyntheticReturnPoint(nextPoints[0], nextPoints[nextPoints.length - 1])) {
    nextPoints.pop();
  }
  return nextPoints;
}

function normalizeComparableText(value) {
  return String(value || '').trim().toLowerCase();
}

function getOrganizationMetadataFromOption(option) {
  return {
    organizationName: option?.organizationLabel || option?.label || '',
    siteName: option?.isPrimaryAddress ? '' : (option?.siteName || ''),
  };
}

export default function IKFormDialog({
  open,
  onClose,
  onSaved,
  ticketOptions = [],
  periodTimeEntries = [],
  existingEntries = [],
  addressOptions = [],
  meConfig = { fiscalCv: null, mainAddress: null },
  editing = null,
  api,
  mapOnlyMode = false,
  allowLockedEdit = false,
}) {
  const isLocked = Boolean(editing?._id && editing?.status !== 'draft' && !allowLockedEdit);
  const isMapOnlyEdit = Boolean(mapOnlyMode && editing?._id);
  const [form, setForm] = useState({
    ticketId: '',
    timeEntryId: '',
    vehicleFiscalCv: '',
    roundTrip: false,
    points: [emptyPoint(0), emptyPoint(1)],
    notes: '',
    status: 'draft',
  });
  const [previewGeometry, setPreviewGeometry] = useState(null);
  const [previewPoints, setPreviewPoints] = useState([]);
  const [routeAlternatives, setRouteAlternatives] = useState([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [expenses, setExpenses] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const timeEntriesByTicket = useMemo(() => {
    const map = new Map();
    for (const te of periodTimeEntries || []) {
      const id = Number(te.ticketId);
      if (!Number.isFinite(id)) continue;
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(te);
    }
    return map;
  }, [periodTimeEntries]);

  const selectedTicketId = Number(form.ticketId);
  const selectedTimeEntries = Number.isFinite(selectedTicketId)
    ? (timeEntriesByTicket.get(selectedTicketId) || [])
    : [];

  const selectedTimeEntry = selectedTimeEntries.find((te) => te.entryId === Number(form.timeEntryId));
  const selectedClientOrganizationId = useMemo(
    () => (selectedTimeEntry?.Organization ? String(selectedTimeEntry.Organization) : ''),
    [selectedTimeEntry]
  );
  const selectedClientName = useMemo(() => {
    if (!selectedTimeEntry?.Organization) return '';

    const match = addressOptions.find(
      (option) =>
        option.type === 'organization' &&
        String(option.organizationId || '') === String(selectedTimeEntry.Organization)
    );

    return match?.label || '';
  }, [addressOptions, selectedTimeEntry]);

  const inferOrganizationSelectionFromPoint = (point) => {
    const selectionType = normalizeSelectionType(point);
    if (!organizationSelectionTypes.includes(selectionType)) {
      return {
        selectionOrganizationId: '',
        organizationName: '',
        siteName: '',
        address: point?.address || '',
      };
    }

    const options = addressOptions.filter((option) => (
      option.type === 'organization'
      && Array.isArray(option.organizationTypes)
      && option.organizationTypes.includes(selectionType)
    ));

    const normalizedAddress = normalizeComparableText(point?.address);
    const normalizedLabel = normalizeComparableText(point?.label);

    const matchedOption = options.find((option) => normalizeComparableText(option.address) === normalizedAddress)
      || options.find((option) => normalizeComparableText(option.label) === normalizedLabel)
      || options.find((option) => normalizeComparableText(option.organizationLabel) === normalizedLabel)
      || options.find((option) => normalizeComparableText(option.siteName) === normalizedLabel);

    const selectionOrganizationId = matchedOption?.organizationId
      ? String(matchedOption.organizationId)
      : '';

    if (!selectionOrganizationId) {
      return {
        selectionOrganizationId: '',
        organizationName: '',
        siteName: '',
        address: point?.address || '',
      };
    }

    const organizationOptions = options.filter(
      (option) => String(option.organizationId || '') === selectionOrganizationId
    );

    const matchedAddressOption = organizationOptions.find(
      (option) => normalizeComparableText(option.address) === normalizedAddress
    ) || organizationOptions.find(
      (option) =>
        normalizeComparableText(option.label) === normalizedLabel
        || normalizeComparableText(option.organizationLabel) === normalizedLabel
        || normalizeComparableText(option.siteName) === normalizedLabel
    ) || organizationOptions.find((option) => option.isPrimaryAddress)
      || organizationOptions[0];

    return {
      selectionOrganizationId,
      organizationName: matchedAddressOption?.organizationLabel || matchedOption?.organizationLabel || '',
      siteName: matchedAddressOption?.isPrimaryAddress ? '' : (matchedAddressOption?.siteName || ''),
      address: matchedAddressOption?.address || point?.address || '',
    };
  };

  useEffect(() => {
    if (!editing) {
      setForm({
        ticketId: '',
        timeEntryId: '',
        vehicleFiscalCv: '',
        roundTrip: false,
        points: [emptyPoint(0), emptyPoint(1)],
        notes: '',
        status: 'draft',
      });
      setPreviewGeometry(null);
      setPreviewPoints([]);
      setRouteAlternatives([]);
      setSelectedRouteIndex(0);
      setExpenses([]);
      return;
    }

    setForm({
      ticketId: editing.ticketId ?? '',
      timeEntryId: editing.timeEntryId ?? '',
      vehicleFiscalCv: editing.vehicleFiscalCv ?? '',
      roundTrip: Boolean(editing.roundTrip),
      points: stripSyntheticReturnPoint(editing.points || []).map((p, i) => {
        const inferredOrganizationSelection = inferOrganizationSelectionFromPoint(p);

        return {
          label: p.label || getDefaultPointLabel(i),
          labelMode: 'custom',
          address: inferredOrganizationSelection.address,
          organizationName: p.organizationName || inferredOrganizationSelection.organizationName || '',
          siteName: p.siteName || inferredOrganizationSelection.siteName || '',
          selectionOrganizationId: inferredOrganizationSelection.selectionOrganizationId,
          selectionType: normalizeSelectionType(p),
          sourceType: p.sourceType || 'manual',
        };
      }),
      notes: editing.notes || '',
      status: editing.status || 'draft',
    });
    setPreviewGeometry(editing.mapboxRouteGeometry || null);
    setPreviewPoints(editing.points || []);
    setRouteAlternatives([]);
    setSelectedRouteIndex(Number(editing.routeIndex || 0));
    setExpenses(
      (editing.expenses || []).map((expense) => ({
        type: expense.type || 'meal',
        amountPaid: expense.amountPaid ?? '',
        reimbursedAmount: expense.reimbursedAmount ?? 0,
        receiptOriginalName: expense.receiptOriginalName || null,
        receiptPath: expense.receiptPath || null,
        file: null,
      }))
    );
  }, [addressOptions, editing, open]);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        if (!process.env.REACT_APP_MAPBOX_TOKEN) {
          setPreviewGeometry(null);
          setRouteAlternatives([]);
          return;
        }

        const usable = form.points.filter((p) => p.address?.trim());
        if (usable.length < 2) {
          setPreviewGeometry(null);
          setPreviewPoints([]);
          setRouteAlternatives([]);
          return;
        }

        const geocoded = [];
        for (const p of usable) {
          const q = encodeURIComponent(p.address);
          const g = await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${q}.json`, {
            params: { access_token: process.env.REACT_APP_MAPBOX_TOKEN, limit: 1 },
          });
          const center = g.data?.features?.[0]?.center;
          if (!center) {
            setPreviewGeometry(null);
            setRouteAlternatives([]);
            return;
          }
          geocoded.push({ ...p, coordinates: { lng: center[0], lat: center[1] } });
        }

        const routePoints = [...geocoded];
        const start = routePoints[0];
        const end = routePoints[routePoints.length - 1];
        const sameStartEnd =
          start && end && start.coordinates.lng === end.coordinates.lng && start.coordinates.lat === end.coordinates.lat;

        if (form.roundTrip && !sameStartEnd) {
          routePoints.push({ ...start, label: `${start.label || 'Point 1'} (Retour)` });
        }

        const coordsStr = routePoints.map((p) => `${p.coordinates.lng},${p.coordinates.lat}`).join(';');
        const response = await axios.get(`https://api.mapbox.com/directions/v5/mapbox/driving/${coordsStr}`, {
          params: {
            access_token: process.env.REACT_APP_MAPBOX_TOKEN,
            geometries: 'polyline',
            overview: 'full',
            alternatives: true,
          },
        });

        const routes = Array.isArray(response.data?.routes) ? response.data.routes : [];
        const nextRouteAlternatives = routes.map((route, routeIndex) => ({
          index: routeIndex,
          geometry: route.geometry || null,
          distanceKm: Number(((route.distance || 0) / 1000).toFixed(2)),
          durationMinutes: Number(((route.duration || 0) / 60).toFixed(0)),
        }));

        setPreviewPoints(routePoints);
        setRouteAlternatives(nextRouteAlternatives);
        setSelectedRouteIndex((current) => (nextRouteAlternatives[current] ? current : 0));
        if (nextRouteAlternatives.length === 0) {
          setPreviewGeometry(null);
        }
      } catch {
        setRouteAlternatives([]);
        setPreviewGeometry(null);
      }
    };

    if (open) fetchPreview();
  }, [form.points, form.roundTrip, open]);

  useEffect(() => {
    if (routeAlternatives.length > 0) {
      const selectedRoute = routeAlternatives[selectedRouteIndex] || routeAlternatives[0] || null;
      setPreviewGeometry(selectedRoute?.geometry || null);
    }
  }, [routeAlternatives, selectedRouteIndex]);

  useEffect(() => {
    if (!open || editing || !selectedClientName || !selectedClientOrganizationId) return;

    setForm((prev) => {
      if (!Array.isArray(prev.points) || prev.points.length === 0) return prev;

      const points = [...prev.points];
      let hasChanged = false;

      points.forEach((point, index) => {
        if (normalizeSelectionType(point) !== 'client') return;

        const organizationAddressOptions = getOrganizationAddressOptions('client')
          .filter((option) => String(option.organizationId || '') === selectedClientOrganizationId);
        const preferredAddress = organizationAddressOptions.find((option) => option.isPrimaryAddress)?.address
          || organizationAddressOptions[0]?.address
          || '';

        const updatedPoint = {
          ...point,
          selectionOrganizationId: selectedClientOrganizationId,
          organizationName: selectedClientName,
          siteName: organizationAddressOptions.find((option) => option.address === preferredAddress)?.isPrimaryAddress
            ? ''
            : (organizationAddressOptions.find((option) => option.address === preferredAddress)?.siteName || ''),
          address: preferredAddress || point.address || '',
        };

        if (updatedPoint.labelMode !== 'custom') {
          updatedPoint.label = selectedClientName;
        }

        if (
          updatedPoint.selectionOrganizationId !== point.selectionOrganizationId
          || updatedPoint.address !== point.address
          || updatedPoint.label !== point.label
        ) {
          points[index] = updatedPoint;
          hasChanged = true;
        }
      });

      return hasChanged ? { ...prev, points } : prev;
    });
  }, [editing, open, selectedClientName, selectedClientOrganizationId, addressOptions]);

  const updatePoint = (idx, key, value) => {
    setForm((prev) => {
      const points = [...prev.points];
      points[idx] = { ...points[idx], [key]: value };
      return { ...prev, points };
    });
  };

  const handleTicketChange = (value) => setForm((p) => ({ ...p, ticketId: value, timeEntryId: '' }));
  const addPoint = () => setForm((p) => ({ ...p, points: [...p.points, emptyPoint(p.points.length)] }));
  const removePoint = (idx) =>
    setForm((p) => (p.points.length <= 2 ? p : { ...p, points: p.points.filter((_, i) => i !== idx) }));
  const addExpense = () => setExpenses((prev) => [...prev, { type: 'meal', amountPaid: '', reimbursedAmount: 0, receiptOriginalName: null, receiptPath: null, file: null }]);
  const removeExpense = (idx) => setExpenses((prev) => prev.filter((_, i) => i !== idx));

  const updateExpense = (idx, key, value) => {
    setExpenses((prev) => {
      const next = [...prev];
      const current = next[idx] || {};
      const updated = { ...current, [key]: value };
      const numericAmount = Number(updated.amountPaid);
      updated.reimbursedAmount = Number.isFinite(numericAmount)
        ? (updated.type === 'meal' ? Math.min(numericAmount, 15) : numericAmount)
        : 0;
      next[idx] = updated;
      return next;
    });
  };

  const downloadExpenseReceipt = async (expenseIndex, fileName) => {
    if (!editing?._id) return;
    try {
      const response = await api.get(`/api/ik/${editing._id}/expenses/${expenseIndex}/receipt`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || `justificatif-${expenseIndex + 1}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      alert(e?.response?.data?.error || 'Impossible de telecharger le justificatif.');
    }
  };

  const homeAddress = useMemo(() => {
    if (meConfig?.mainAddress) return meConfig.mainAddress;
    const homeOption = addressOptions.find((option) => option.type === 'home');
    return homeOption?.address || '';
  }, [addressOptions, meConfig?.mainAddress]);

  const getOrganizationAddressOptions = (selectionType) => {
    return addressOptions.filter((option) => {
      if (option.type !== 'organization') return false;
      if (!organizationSelectionTypes.includes(selectionType)) return false;
      return Array.isArray(option.organizationTypes) && option.organizationTypes.includes(selectionType);
    });
  };

  const getOrganizationEntityOptions = (selectionType) => {
    const map = new Map();

    getOrganizationAddressOptions(selectionType).forEach((option) => {
      const organizationId = String(option.organizationId || '');
      if (!organizationId) return;
      if (!map.has(organizationId)) {
        map.set(organizationId, {
          organizationId,
          label: option.organizationLabel || option.label || 'Organisation',
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  };

  const getSelectedOrganizationId = (point) => {
    if (point?.selectionOrganizationId) return String(point.selectionOrganizationId);
    const match = getOrganizationAddressOptions(normalizeSelectionType(point)).find(
      (option) => option.address === point.address
    );
    return match?.organizationId ? String(match.organizationId) : '';
  };

  const getOrganizationAddressOptionsForPoint = (point) => {
    const selectionType = normalizeSelectionType(point);
    const organizationId = getSelectedOrganizationId(point);

    if (!organizationId) return [];

    return getOrganizationAddressOptions(selectionType)
      .filter((option) => String(option.organizationId || '') === organizationId)
      .sort((a, b) => {
        if (a.isPrimaryAddress && !b.isPrimaryAddress) return -1;
        if (!a.isPrimaryAddress && b.isPrimaryAddress) return 1;
        return String(a.label || '').localeCompare(String(b.label || ''));
      });
  };

  const getAutomaticLabelForPoint = (point, idx, overrides = {}) => {
    const mergedPoint = { ...point, ...overrides };
    const selectionType = normalizeSelectionType(mergedPoint);

    if (selectionType === 'home') {
      return 'Domicile';
    }

    if (organizationSelectionTypes.includes(selectionType)) {
      const organizationId = String(
        overrides.selectionOrganizationId
        ?? mergedPoint.selectionOrganizationId
        ?? getSelectedOrganizationId(mergedPoint)
        ?? ''
      );
      const address = String(overrides.address ?? mergedPoint.address ?? '');

      const organizationOption = getOrganizationEntityOptions(selectionType).find(
        (option) => option.organizationId === organizationId
      );
      const addressOption = getOrganizationAddressOptions(selectionType).find(
        (option) => String(option.organizationId || '') === organizationId && option.address === address
      );

      if (addressOption) {
        if (addressOption.isPrimaryAddress) {
          return addressOption.organizationLabel || addressOption.label || organizationOption?.label || selectionTypeLabels[selectionType];
        }
        return addressOption.siteName || addressOption.label || organizationOption?.label || selectionTypeLabels[selectionType];
      }

      return organizationOption?.label || selectionTypeLabels[selectionType] || getDefaultPointLabel(idx);
    }

    if (selectionType === 'manual') {
      return getDefaultPointLabel(idx);
    }

    return selectionTypeLabels[selectionType] || getDefaultPointLabel(idx);
  };

  const getPointDisplayName = (point, idx) => {
    const selectionType = normalizeSelectionType(point);

    if (selectionType === 'home') {
      return 'Domicile';
    }

    if (organizationSelectionTypes.includes(selectionType)) {
      const organizationId = getSelectedOrganizationId(point);
      const organization = getOrganizationEntityOptions(selectionType).find(
        (option) => option.organizationId === organizationId
      );
      return organization?.label || selectionTypeLabels[selectionType] || getDefaultPointLabel(idx);
    }

    if (selectionType === 'manual') {
      return 'Point manuel';
    }

    return selectionTypeLabels[selectionType] || getDefaultPointLabel(idx);
  };

  const existingTicketIds = useMemo(
    () => new Set(
      (existingEntries || [])
        .filter((entry) => String(entry?._id) !== String(editing?._id || ''))
        .map((entry) => Number(entry?.ticketId))
        .filter((ticketId) => Number.isFinite(ticketId))
    ),
    [editing?._id, existingEntries]
  );

  const existingTimeEntryIds = useMemo(
    () => new Set(
      (existingEntries || [])
        .filter((entry) => String(entry?._id) !== String(editing?._id || ''))
        .map((entry) => Number(entry?.timeEntryId))
        .filter((timeEntryId) => Number.isFinite(timeEntryId))
    ),
    [editing?._id, existingEntries]
  );

  const selectedTicketTimeEntryStats = useMemo(() => {
    const total = selectedTimeEntries.length;
    const entered = selectedTimeEntries.reduce((count, timeEntry) => (
      existingTimeEntryIds.has(Number(timeEntry?.entryId)) ? count + 1 : count
    ), 0);

    return { total, entered };
  }, [existingTimeEntryIds, selectedTimeEntries]);

  const ticketTimeEntryStatsByTicket = useMemo(() => {
    const statsMap = new Map();

    ticketOptions.forEach((ticketOption) => {
      const ticketId = Number(ticketOption?.value);
      if (!Number.isFinite(ticketId)) return;

      const ticketTimeEntries = timeEntriesByTicket.get(ticketId) || [];
      const total = ticketTimeEntries.length;
      const entered = ticketTimeEntries.reduce((count, timeEntry) => (
        existingTimeEntryIds.has(Number(timeEntry?.entryId)) ? count + 1 : count
      ), 0);

      statsMap.set(ticketId, { total, entered });
    });

    return statsMap;
  }, [existingTimeEntryIds, ticketOptions, timeEntriesByTicket]);

  const missingReceiptIndex = useMemo(
    () => expenses.findIndex((expense) => !expense.file && !expense.receiptPath),
    [expenses]
  );
  const isSelectedTimeEntryAlreadyEntered = useMemo(
    () => existingTimeEntryIds.has(Number(form.timeEntryId)),
    [existingTimeEntryIds, form.timeEntryId]
  );

  const hasInvalidPoints = useMemo(
    () => (form.points || []).length < 2 || form.points.some((point) => !String(point?.address || '').trim()),
    [form.points]
  );

  const isFormValid = useMemo(() => (
    Number.isFinite(Number(form.ticketId))
    && Number.isFinite(Number(form.timeEntryId))
    && !isSelectedTimeEntryAlreadyEntered
    && !hasInvalidPoints
    && missingReceiptIndex === -1
    && !isLocked
  ), [form.ticketId, form.timeEntryId, hasInvalidPoints, isLocked, isSelectedTimeEntryAlreadyEntered, missingReceiptIndex]);

  const handleSelectionTypeChange = (idx, selectionType) => {
    setForm((prev) => {
      const points = [...prev.points];
      const currentPoint = points[idx] || emptyPoint(idx);

      if (!selectionType) {
        points[idx] = {
          ...currentPoint,
          labelMode: currentPoint.labelMode || 'auto',
          organizationName: '',
          siteName: '',
          selectionOrganizationId: '',
          selectionType: '',
          sourceType: 'manual',
          address: '',
        };
        if (points[idx].labelMode !== 'custom') {
          points[idx].label = getAutomaticLabelForPoint(points[idx], idx);
        }
        return { ...prev, points };
      }

      if (selectionType === 'home') {
        points[idx] = {
          ...currentPoint,
          organizationName: '',
          siteName: '',
          selectionOrganizationId: '',
          selectionType,
          sourceType: 'home',
          address: homeAddress || '',
          labelMode: currentPoint.labelMode || 'auto',
        };
        if (points[idx].labelMode !== 'custom') {
          points[idx].label = getAutomaticLabelForPoint(points[idx], idx);
        }
        return { ...prev, points };
      }

      if (organizationSelectionTypes.includes(selectionType)) {
        const availableOptions = getOrganizationAddressOptions(selectionType);
        const currentAddressStillValid = availableOptions.some((option) => option.address === currentPoint.address);

        points[idx] = {
          ...currentPoint,
          organizationName: '',
          siteName: '',
          selectionOrganizationId: currentAddressStillValid ? getSelectedOrganizationId(currentPoint) : '',
          selectionType,
          sourceType: 'organization',
          address: currentAddressStillValid ? currentPoint.address : '',
          labelMode: currentPoint.labelMode || 'auto',
        };
        if (points[idx].labelMode !== 'custom') {
          points[idx].label = getAutomaticLabelForPoint(points[idx], idx);
        }
        return { ...prev, points };
      }

      points[idx] = {
        ...currentPoint,
        organizationName: '',
        siteName: '',
        labelMode: currentPoint.labelMode || 'auto',
        selectionOrganizationId: '',
        selectionType: 'manual',
        sourceType: 'manual',
      };
      if (points[idx].labelMode !== 'custom') {
        points[idx].label = getAutomaticLabelForPoint(points[idx], idx);
      }
      return { ...prev, points };
    });
  };

  const toggleLabelMode = (idx) => {
    setForm((prev) => {
      const points = [...prev.points];
      const currentPoint = points[idx] || emptyPoint(idx);
      const nextMode = currentPoint.labelMode === 'custom' ? 'auto' : 'custom';
      const updatedPoint = {
        ...currentPoint,
        labelMode: nextMode,
      };

      if (nextMode === 'auto') {
        updatedPoint.label = getAutomaticLabelForPoint(updatedPoint, idx);
      }

      points[idx] = updatedPoint;
      return { ...prev, points };
    });
  };

  const save = async () => {
    try {
      setIsSaving(true);

      const missingReceiptIndex = expenses.findIndex((expense) => !expense.file && !expense.receiptPath);
      if (missingReceiptIndex >= 0) {
        throw new Error(`Frais ${missingReceiptIndex + 1}: justificatif requis`);
      }

      const payload = {
        ticketId: Number(form.ticketId),
        timeEntryId: Number(form.timeEntryId),
        vehicleFiscalCv: form.vehicleFiscalCv !== '' ? Number(form.vehicleFiscalCv) : undefined,
        roundTrip: Boolean(form.roundTrip),
        routeIndex: selectedRouteIndex,
        startPointType: form.points?.[0]?.sourceType || 'manual',
        points: form.points,
        expenses: expenses.map((expense) => ({
          type: expense.type,
          amountPaid: Number(expense.amountPaid || 0),
          receiptOriginalName: expense.receiptOriginalName || null,
        })),
        notes: form.notes || null,
        status: form.status,
      };

      if (!Number.isFinite(payload.ticketId)) throw new Error('Ticket requis');
      if (!Number.isFinite(payload.timeEntryId)) throw new Error('Temps passé requis');
      if (payload.points.length < 2) throw new Error('2 points minimum');

      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (key === 'points' || key === 'expenses') {
          formData.append(key, JSON.stringify(value));
        } else if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      expenses.forEach((expense, idx) => {
        if (expense.file) {
          formData.append(`expense_receipt_${idx}`, expense.file);
        }
      });

      if (editing?._id) {
        await api.put(`/api/ik/${editing._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/api/ik', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      onSaved?.();
    } catch (e) {
      alert(e?.response?.data?.error || e.message || 'Erreur sauvegarde IK');
    } finally {
      setIsSaving(false);
    }
  };

  const readOnlyDate = selectedTimeEntry?.spentOn
    ? fmtDate(selectedTimeEntry.spentOn)
    : editing?.dateTrajet
      ? fmtDate(editing.dateTrajet)
      : '—';
  const readOnlyActivity = selectedTimeEntry?.activity || editing?.activity || '—';
  const readOnlyComments = selectedTimeEntry?.comments || editing?.comments || '—';
  const extraExpensesAmount = Number(expenses.reduce((sum, expense) => sum + Number(expense.amountPaid || 0), 0).toFixed(2));
  const extraExpensesReimbursedAmount = Number(expenses.reduce((sum, expense) => sum + Number(expense.reimbursedAmount || 0), 0).toFixed(2));
  const currentDistanceAmount = Number(editing?.amount || 0);
  const currentDistanceKm = Number(editing?.distanceKm || 0);
  const totalPreviewAmount = Number((currentDistanceAmount + extraExpensesReimbursedAmount).toFixed(2));
  const selectedRouteAlternative = routeAlternatives[selectedRouteIndex] || routeAlternatives[0] || null;
  const previewDistanceKm = selectedRouteAlternative?.distanceKm ?? currentDistanceKm;
  const previewDurationMinutes = selectedRouteAlternative?.durationMinutes ?? 0;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>
        {isMapOnlyEdit
          ? 'Modifier trajet IK'
          : (editing?._id ? 'Modifier entrée IK' : 'Nouvelle entrée IK')}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.35fr) minmax(380px, 0.85fr)' },
            minHeight: { xs: 'auto', xl: 'calc(100vh - 130px)' },
          }}
        >
          <Box sx={{ p: 3, overflowY: 'auto' }}>
            <Stack spacing={2}>
              {isLocked ? (
                <Typography variant="body2" color="warning.main">
                  Cette IK est en statut {editing?.status === 'submitted' ? 'envoye' : 'valide'} et n est plus modifiable.
                </Typography>
              ) : null}

              {isMapOnlyEdit ? (
                <>
                  <TextField
                    label="Ticket"
                    value={editing?.ticketId ? `#${editing.ticketId}` : '—'}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                  <TextField
                    label="Temps passé"
                    value={editing?.timeEntryId ? `#${editing.timeEntryId}` : '—'}
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                </>
              ) : (
                <>
                  <TextField select label="Ticket" value={form.ticketId} onChange={(e) => handleTicketChange(e.target.value)} fullWidth>
                    {ticketOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                          <Typography variant="body2">{o.label}</Typography>
                          {(() => {
                            const stats = ticketTimeEntryStatsByTicket.get(Number(o.value));
                            if (!stats?.total) return null;

                            return (
                              <Chip
                                size="small"
                                color={stats.entered > 0 ? 'success' : 'default'}
                                variant="outlined"
                                label={`${stats.entered}/${stats.total} déjà saisi`}
                              />
                            );
                          })()}
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    label="Temps passé"
                    value={form.timeEntryId}
                    onChange={(e) => setForm((p) => ({ ...p, timeEntryId: e.target.value }))}
                    fullWidth
                    disabled={!form.ticketId}
                    helperText={
                      form.ticketId
                        ? `${selectedTicketTimeEntryStats.entered} / ${selectedTicketTimeEntryStats.total} temps passés déjà saisis en IK`
                        : 'Sélectionne un ticket pour voir les temps passés disponibles'
                    }
                  >
                    {selectedTimeEntries.map((te) => (
                      <MenuItem key={te.entryId} value={te.entryId}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                          <Typography variant="body2">
                            #{te.entryId} • {fmtDate(te.spentOn)} • {Number(te.hours || 0).toFixed(2)}h • {te.projectName || 'Projet'}
                          </Typography>
                          {existingTimeEntryIds.has(Number(te.entryId)) ? (
                            <Chip size="small" color="success" variant="outlined" label="Déjà saisi" />
                          ) : null}
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}

              <TextField
                label="Date trajet"
                value={readOnlyDate}
                InputProps={{ readOnly: true }}
                helperText="Date issue du temps passé sélectionné (non modifiable)"
                fullWidth
              />

              <TextField
                label="Activité"
                value={readOnlyActivity}
                InputProps={{ readOnly: true }}
                fullWidth
              />

              <TextField
                label="Commentaire temps passé"
                value={readOnlyComments}
                InputProps={{ readOnly: true }}
                multiline
                minRows={2}
                fullWidth
              />

              {!isMapOnlyEdit && isSelectedTimeEntryAlreadyEntered ? (
                <Alert severity="warning">
                  Ce temps passé est déjà saisi dans une IK. Sélectionne un autre temps passé pour pouvoir enregistrer.
                </Alert>
              ) : null}

              <FormControlLabel
                control={<Switch checked={form.roundTrip} onChange={(e) => setForm((p) => ({ ...p, roundTrip: e.target.checked }))} />}
                label="Aller-retour"
              />

              <Divider />
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RouteIcon fontSize="small" />
                Points trajet
              </Typography>

              {routeAlternatives.length > 0 ? (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#f8fbff' }}>
                  <Stack spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      Choix de l&apos;itinéraire
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {routeAlternatives.map((route) => (
                        <Button
                          key={`route-inline-${route.index}`}
                          variant={route.index === selectedRouteIndex ? 'contained' : 'outlined'}
                          onClick={() => setSelectedRouteIndex(route.index)}
                          sx={{ textTransform: 'none' }}
                        >
                          {`Chemin ${route.index + 1} • ${fmtKm(route.distanceKm)} • ${fmtMinutes(route.durationMinutes)}`}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              ) : null}

              {form.points.map((pt, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                  {(() => {
                    const pointSelectionType = normalizeSelectionType(pt);
                    const PointTypeIcon = selectionTypeIcons[pointSelectionType] || PlaceIcon;

                    return (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
                    <Stack
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ width: { xs: '100%', md: 120 }, flexShrink: 0 }}
                    >
                      <Avatar sx={{ bgcolor: '#1d4ed8', width: 44, height: 44, fontWeight: 700 }}>
                        {i + 1}
                      </Avatar>
                      {pt.labelMode === 'custom' ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
                          <TextField
                            size="small"
                            value={pt.label}
                            onChange={(e) => updatePoint(i, 'label', e.target.value)}
                            placeholder={`Point ${i + 1}`}
                            sx={{ width: '100%' }}
                            inputProps={{ style: { textAlign: 'center', fontWeight: 600 } }}
                          />
                          <Tooltip title="Revenir au libellé automatique">
                            <IconButton onClick={() => toggleLabelMode(i)} size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
                          <Typography
                            variant="body2"
                            sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}
                          >
                            {pt.label || getAutomaticLabelForPoint(pt, i)}
                          </Typography>
                          <Tooltip title="Modifier le libellé">
                            <IconButton onClick={() => toggleLabelMode(i)} size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>

                    <Stack spacing={1.5} sx={{ flex: 1 }}>
                      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1} alignItems={{ xs: 'stretch', xl: 'center' }}>
                        <TextField
                          select
                          label="Type de déplacement"
                          value={normalizeSelectionType(pt)}
                          onChange={(e) => handleSelectionTypeChange(i, e.target.value)}
                          sx={{ width: { xs: '100%', xl: 190 } }}
                        >
                          <MenuItem value="">Sélectionner</MenuItem>
                          <MenuItem value="home">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <HomeIcon fontSize="small" />
                              <span>Domicile</span>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="client">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CorporateFareIcon fontSize="small" />
                              <span>Client</span>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="fournisseur">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <LocalShippingIcon fontSize="small" />
                              <span>Fournisseur</span>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="partenaire">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <HandshakeIcon fontSize="small" />
                              <span>Partenaire</span>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="manual">
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PlaceIcon fontSize="small" />
                              <span>Manuel</span>
                            </Stack>
                          </MenuItem>
                        </TextField>

                        {organizationSelectionTypes.includes(normalizeSelectionType(pt)) ? (
                          <Autocomplete
                            options={getOrganizationEntityOptions(normalizeSelectionType(pt))}
                            value={
                              getOrganizationEntityOptions(normalizeSelectionType(pt)).find(
                                (option) => option.organizationId === getSelectedOrganizationId(pt)
                              ) || null
                            }
                            onChange={(_, selectedOption) => {
                              setForm((prev) => {
                                const points = [...prev.points];
                                const currentPoint = points[i] || emptyPoint(i);
                                const updatedPoint = {
                                  ...currentPoint,
                                  selectionOrganizationId: selectedOption?.organizationId || '',
                                  organizationName: selectedOption?.label || '',
                                  siteName: '',
                                  address: '',
                                };

                                if (updatedPoint.labelMode !== 'custom') {
                                  updatedPoint.label = getAutomaticLabelForPoint(updatedPoint, i);
                                }

                                points[i] = updatedPoint;
                                return { ...prev, points };
                              });
                            }}
                            getOptionLabel={(option) => option?.label || ''}
                            isOptionEqualToValue={(option, value) => option.organizationId === value.organizationId}
                            sx={{ width: { xs: '100%', xl: 260 }, flexShrink: 0 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={`Nom ${selectionTypeLabels[normalizeSelectionType(pt)] || 'organisation'}`}
                              />
                            )}
                          />
                        ) : (
                          <TextField
                            label="Nom"
                            value={getPointDisplayName(pt, i)}
                            InputProps={{ readOnly: true }}
                            sx={{ width: { xs: '100%', xl: 260 }, flexShrink: 0 }}
                          />
                        )}

                        {normalizeSelectionType(pt) === 'home' ? (
                          <TextField
                            label="Adresse"
                            value={homeAddress || pt.address || ''}
                            InputProps={{ readOnly: true }}
                            helperText="Adresse principale du profil utilisateur"
                            sx={{ flex: 1 }}
                          />
                        ) : null}

                        {organizationSelectionTypes.includes(normalizeSelectionType(pt)) ? (
                          <Autocomplete
                            options={getOrganizationAddressOptionsForPoint(pt)}
                            value={
                              getOrganizationAddressOptionsForPoint(pt).find(
                                (option) => option.address === pt.address
                              ) || null
                            }
                            onChange={(_, selectedOption) => {
                              setForm((prev) => {
                                const points = [...prev.points];
                                const currentPoint = points[i] || emptyPoint(i);
                                const updatedPoint = {
                                  ...currentPoint,
                                  address: selectedOption?.address || '',
                                  selectionOrganizationId: selectedOption?.organizationId || getSelectedOrganizationId(currentPoint),
                                  ...getOrganizationMetadataFromOption(selectedOption),
                                };

                                if (updatedPoint.labelMode !== 'custom') {
                                  updatedPoint.label = getAutomaticLabelForPoint(updatedPoint, i);
                                }

                                points[i] = updatedPoint;
                                return { ...prev, points };
                              });
                            }}
                            getOptionLabel={(option) => option?.address || ''}
                            isOptionEqualToValue={(option, value) => option.address === value.address}
                            renderOption={(props, option) => (
                              <li {...props} key={`${option.organizationId || option.label}-${option.address}`}>
                                {option.label} - {option.address}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Adresse"
                                helperText="Adresse principale + sites rattachés"
                              />
                            )}
                            disabled={!getSelectedOrganizationId(pt)}
                            sx={{ flex: 1 }}
                          />
                        ) : null}

                        {normalizeSelectionType(pt) === 'manual' ? (
                          <TextField
                            label="Adresse"
                            value={pt.address}
                            onChange={(e) => updatePoint(i, 'address', e.target.value)}
                            sx={{ flex: 1 }}
                          />
                        ) : null}

                        <IconButton color="error" onClick={() => removePoint(i)} sx={{ alignSelf: { xs: 'flex-end', xl: 'center' } }}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Stack>
                    );
                  })()}
                </Paper>
              ))}

              <Button variant="outlined" onClick={addPoint}>
                Ajouter un point
              </Button>

              {isMapOnlyEdit ? null : (
                <>
                  <Divider />
                  <Typography variant="subtitle2">Frais supplémentaires</Typography>

                  {expenses.map((expense, idx) => (
                    <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
                      <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', xl: 'flex-start' }}>
                        <TextField
                          select
                          label="Type de frais"
                          value={expense.type}
                          onChange={(e) => updateExpense(idx, 'type', e.target.value)}
                          sx={{ width: { xs: '100%', xl: 180 } }}
                        >
                          <MenuItem value="meal">Repas</MenuItem>
                          <MenuItem value="parking">Parc mètre</MenuItem>
                        </TextField>

                        <TextField
                          label="Prix payé"
                          type="number"
                          value={expense.amountPaid}
                          onChange={(e) => updateExpense(idx, 'amountPaid', e.target.value)}
                          helperText={expense.type === 'meal' ? 'Remboursement plafonné à 15€' : 'Remboursement au réel'}
                          sx={{ width: { xs: '100%', xl: 180 } }}
                        />

                        <TextField
                          label="Remboursé"
                          value={Number(expense.reimbursedAmount || 0).toFixed(2)}
                          InputProps={{ readOnly: true }}
                          sx={{ width: { xs: '100%', xl: 150 } }}
                        />

                        <Stack spacing={0.5} sx={{ flex: 1, minWidth: { xs: '100%', xl: 280 } }}>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<AttachFileIcon />}
                            color={!expense.file && !expense.receiptPath ? 'error' : 'primary'}
                            sx={{ justifyContent: 'flex-start' }}
                          >
                            {expense.receiptOriginalName || expense.file?.name || 'Uploader justificatif'}
                            <input
                              hidden
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => updateExpense(idx, 'file', e.target.files?.[0] || null)}
                            />
                          </Button>
                          <Typography variant="caption" color={!expense.file && !expense.receiptPath ? 'error.main' : 'text.secondary'}>
                            {expense.type === 'meal'
                              ? 'Justificatif obligatoire. Facture ou ticket repas accepte en image ou PDF.'
                              : 'Justificatif obligatoire. Ticket ou facture accepte en image ou PDF.'}
                          </Typography>
                        </Stack>

                        {expense.receiptPath ? (
                          <Button
                            variant="text"
                            startIcon={<DownloadIcon />}
                            sx={{ alignSelf: { xs: 'flex-start', xl: 'center' }, whiteSpace: 'nowrap' }}
                            onClick={() => downloadExpenseReceipt(
                              idx,
                              expense.receiptOriginalName || `justificatif-${idx + 1}`
                            )}
                          >
                            Telecharger
                          </Button>
                        ) : null}

                        <IconButton color="error" onClick={() => removeExpense(idx)} sx={{ alignSelf: { xs: 'flex-end', xl: 'center' } }}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Paper>
                  ))}

                  <Button variant="outlined" onClick={addExpense}>
                    Ajouter un frais
                  </Button>

                  <TextField
                    label="Notes"
                    multiline
                    minRows={3}
                    value={form.notes}
                    onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                    fullWidth
                  />
                </>
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              borderLeft: { xs: 'none', xl: '1px solid' },
              borderTop: { xs: '1px solid', xl: 'none' },
              borderColor: 'divider',
              bgcolor: '#f8fafc',
              p: 3,
            }}
          >
            <Stack spacing={2} sx={{ position: { xs: 'static', xl: 'sticky' }, top: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Aperçu du trajet
              </Typography>

              <Box
                sx={{
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: '#dbe4f0',
                  bgcolor: '#fff',
                }}
              >
                <IKRouteMap geometry={previewGeometry} points={previewPoints} height={420} />
              </Box>

              <Stack spacing={1.25}>
                <Typography variant="body2">KM actuels: {fmtKm(previewDistanceKm)}</Typography>
                {routeAlternatives.length > 0 ? (
                  <Typography variant="body2">Durée estimée: {fmtMinutes(previewDurationMinutes)}</Typography>
                ) : null}
                <Typography variant="body2">Déplacement actuel: {currentDistanceAmount.toFixed(2)} €</Typography>
                <Typography variant="body2">Frais saisis: {extraExpensesAmount.toFixed(2)} €</Typography>
                <Typography variant="body2">Frais remboursés: {extraExpensesReimbursedAmount.toFixed(2)} €</Typography>
                <Typography variant="subtitle2">Total remboursé: {totalPreviewAmount.toFixed(2)} €</Typography>
                {!editing ? (
                  <Typography variant="caption" color="text.secondary">
                    Le montant kilométrique exact sera recalculé à l&apos;enregistrement.
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={save} variant="contained" startIcon={<SaveIcon />} disabled={!isFormValid || isSaving}>
          {isSaving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
